import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";
import { Row, Col, Modal } from "antd";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import { MetaDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import { SettingsDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import DetailsHeader from "../../../components/forms/DetailsHeader";
import RadioButtonItem from "../../../components/forms/RadioButtonItem";
import ImageFormItem from "../../../components/forms/ImageFormItem";
import { FormattedMessage } from "react-intl";
import Surface from "../../../components/layouts/Surface";
import Spacer from "../../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../../components/layouts/ResponsiveItemsWrapper";
import { activationConfirmMessage } from "../../../components/forms/formsFeedback";
import { useIntl } from "react-intl";
import { activateRecord, deactivateRecord } from "../employeeDetailsSlice";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";

const PersonalDetails = forwardRef((props, ref) => {
  const { activeEmployee, values } = props;
  const intl = useIntl();
  let { branchId } = useParams();
  const [modal, contextHolder] = Modal.useModal();
  const dispatch = useDispatch();
  const [employeeType, setEmployeeType] = useState(
    activeEmployee.employee_type_id
  );

  const onEmployeeTypeChanged = (val) => {
    setEmployeeType(val);
  };

  const onActivate = (checked) => {
    activationConfirmMessage(
      activeEmployee,
      intl,
      checked,
      activeEmployee.surename + " " + activeEmployee.first_name,
      "employees",
      onActivateConfirmed,
      modal
    );
  };

  const onActivateConfirmed = (record) => {
    const params = {
      employeeId: record.id,
      branchId: branchId,
    };
    const toDispatch = record.active ? deactivateRecord : activateRecord;
    return dispatch(toDispatch(params));
  };

  const disabledDate = (current) => {
    const startdate = moment().subtract(16, "years");
    return current && current > startdate;
  };
  const defaultPickerValue = moment().subtract(16, "years");
  const isIsraeli = employeeType
    ? employeeType !== 2
    : activeEmployee.employee_type_id !== 2;
  const hasWorkPeriods =
    (activeEmployee?.work_periods && activeEmployee.work_periods.length > 0) ||
    (values.work_periods && values.work_periods.length > 0);
  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface>
        {contextHolder}
        <DetailsHeader
          resource={"employees"}
          action={"editor"}
          editTitle={<FormattedMessage id="app.employees.personalDetails" />}
          hasSwitch={true}
          mode={"edit"}
          hideStatus={!activeEmployee.id}
          isActive={activeEmployee ? activeEmployee.active : false}
          onActivate={branchId && activeEmployee.id ? onActivate : null}
        />

        <Row>
          <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={8}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  required={true}
                  name="surename"
                  category="employee"
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <InputItem
                  required={true}
                  name="first_name"
                  category="employee"
                />
              </div>
            </ResponsiveItemsWrapper>

            {!isIsraeli ? (
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <InputItem
                    name="foreign.surename"
                    required={true}
                    category="employee"
                    english={true}
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <InputItem
                    name="foreign.first_name"
                    required={true}
                    category="employee"
                    english={true}
                  />
                </div>
              </ResponsiveItemsWrapper>
            ) : null}

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <DatePickerItem
                  required={true}
                  name="birthday"
                  category="employee"
                  disabledDate={disabledDate}
                  defaultPickerValue={defaultPickerValue}
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <DatePickerItem name="immigration_date" category="employee" />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  name="local_id_number"
                  id={true}
                  category="employee"
                  required={
                    isIsraeli &&
                    (hasWorkPeriods || activeEmployee.local_id_number)
                  }
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  required={true}
                  onChange={onEmployeeTypeChanged}
                  name="employee_type_id"
                  category="employee"
                  metadataName={"employee_types"}
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="accounting_info.department_id"
                  category="employee"
                  metadataName={"departments"}
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                {" "}
                <SettingsDataSelect
                  name="service_areas"
                  category="employee"
                  settingsdataName={"system_tables/service_areas"}
                  mode="multiple"
                  filterItems={(data) => {
                    return activeEmployee?.active_branches
                      ? data.filter((i) => {
                          const itemIds = i.branches.map((j) => j.id);
                          const branchIds = activeEmployee.active_branches.map(
                            (k) => k.id
                          );
                          const filteredArray = itemIds.filter((value) =>
                            branchIds.includes(value)
                          );
                          return filteredArray.length > 0;
                        })
                      : [];
                  }}
                />
              </div>
            </ResponsiveItemsWrapper>

            {!isIsraeli ? (
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <InputItem
                    name="foreign.passport_number"
                    passport={true}
                    required={true}
                    category="employee"
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <DatePickerItem
                    name="foreign.passport_expired_at"
                    category="employee"
                  />
                </div>
              </ResponsiveItemsWrapper>
            ) : null}

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="origin_religion_id"
                  category="employee"
                  metadataName={"religions"}
                  identfierId="id"
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="family_status"
                  category="employee"
                  metadataName={"family_status"}
                />
              </div>
            </ResponsiveItemsWrapper>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="foreign.origin_country"
                  category="employee"
                  metadataName={"countries"}
                  identfierId="english_name"
                />
              </div>
              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}></div>
            </ResponsiveItemsWrapper>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={24}
            lg={12}
            xl={{ span: 6, offset: 2 }}
            xxl={{ span: 4, offset: 2 }}
          >
            <ImageFormItem name="avatar_url" />

            <RadioButtonItem
              required={true}
              name="gender_id"
              category="employee"
              genders={true}
              translated={true}
            />
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

PersonalDetails.propTypes = {
  addImage: PropTypes.func,
  uri: PropTypes.string,
  activeEmployee: PropTypes.object,
};

export default PersonalDetails;
